import {
  Box,
  Stack,
  HStack,
  Text,
  Heading,
  Avatar,
  Badge,
  useColorModeValue,
  useBreakpointValue,
} from "@chakra-ui/react";
import { StarIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { useEffect, useRef, useState, useCallback} from "react";
import { useTranslation } from "react-i18next";

// Dummy reviews – βάλε αργότερα πραγματικές από API
const fallbackReviewsEL = [
  {
    author_name: "Panagiotis A.",
    rating: 5,
    text: "Το μοναδικό bowling στην Πάτρα ευχάριστος χώρος για μικρούς και μεγάλους, ακόμα μπορείς να παίξεις μπιλιάρδο ξύλινο ποδοσφαιρακι και πολλά άλλα, ευγενικό προσωπικό και γρήγορη εξυπηρέτηση!",
    time: Math.floor(Date.now() / 1000) - 86400 * 5,
    profile_photo_url: "",
    relative_time_description: "πριν 4 μήνες",
  },
  {
    author_name: "Μαρία Κ.",
    rating: 4,
    text: "Πολύ καθαρά lanes και ωραία μουσική. Θα ξανάρθουμε!",
    time: Math.floor(Date.now() / 1000) - 86400 * 20,
    profile_photo_url: "",
    relative_time_description: "πριν 7 μήνες",
  },
  {
    author_name: "Nikos R.",
    rating: 5,
    text: "Arcade και billiards μαζί με bowling – το τέλειο combo 👌",
    time: Math.floor(Date.now() / 1000) - 86400 * 35,
    profile_photo_url: "",
    relative_time_description: "πριν 2 εβδομάδες",
  },
];

const fallbackReviewsEN = [
  {
    author_name: "Panagiotis A.",
    rating: 5,
    text: "The only bowling center in Patras, a pleasant place for all ages. You can also play billiards, foosball and much more. Friendly staff and fast service!",
    time: Math.floor(Date.now() / 1000) - 86400 * 5,
    profile_photo_url: "",
    relative_time_description: "4 months ago",
  },
  {
    author_name: "Maria K.",
    rating: 4,
    text: "Very clean lanes and nice music. We will come again!",
    time: Math.floor(Date.now() / 1000) - 86400 * 20,
    profile_photo_url: "",
    relative_time_description: "7 months ago",
  },
  {
    author_name: "Nikos R.",
    rating: 5,
    text: "Arcade and billiards together with bowling – the perfect combo 👌",
    time: Math.floor(Date.now() / 1000) - 86400 * 35,
    profile_photo_url: "",
    relative_time_description: "2 weeks ago",
  },
];

function formatDateFromUnix(ts) {
  try {
    return new Date(ts * 1000).toLocaleDateString("el-GR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function ReviewsCarousel({
  reviews,
  auto = true,
  intervalMs = 4500,
}) {
  const { t, i18n } = useTranslation();
  const [index, setIndex] = useState(0);
  const reviewsToShow = reviews || (i18n.language === "en" ? fallbackReviewsEN : fallbackReviewsEL);
  const len = reviewsToShow.length || 0;

  const timerRef = useRef(null);
  const touchStartX = useRef(0);

  const next = useCallback(
    () => setIndex((i) => (i + 1) % len),
    [len]
  );
  
  const prev = () => setIndex((i) => (i - 1 + len) % len);
  const goTo = (i) => setIndex(i);

  // autoplay (παγώνει στο hover/touch)
  useEffect(() => {
    if (!auto || len <= 1) return;
    timerRef.current = setInterval(next, intervalMs);
    return () => clearInterval(timerRef.current);
  }, [auto, intervalMs, len, next]);
  

  const pause = () => timerRef.current && clearInterval(timerRef.current);
  const resume = () => {
    if (auto && len > 1) timerRef.current = setInterval(next, intervalMs);
  };

  // swipe για mobile
  const onTouchStart = (e) => (touchStartX.current = e.touches[0].clientX);
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) (dx < 0 ? next() : prev());
  };

  // responsive αποστάσεις/μεγέθη
  
  // padding για χώρο των βελών + safe area (iOS notch)
  const sidePadPx = useBreakpointValue({ base: 52, md: 64 }); // ~πλάτος βέλους
  const cardBg = useColorModeValue("white", "whiteAlpha.100");

  if (!len) return null;
  const r = reviewsToShow[index];

  
  return (
    <Box
      bg="transparent"
      onMouseEnter={pause}
      onMouseLeave={resume}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      w="100%"
      mx="auto" // Κεντράρισμα
      h="335px" // Σταθερό ύψος
    >
      <Box
        position="relative"
        py={{ base: 4, md: 6 }}
        px={{ base: 4, md: 6 }}
        pl={`calc(${sidePadPx}px + env(safe-area-inset-left, 0px))`}
        pr={`calc(${sidePadPx}px + env(safe-area-inset-right, 0px))`}
        borderRadius="2xl"
        bg={cardBg}
        backdropFilter="blur(6px)"
        boxShadow="0 10px 30px rgba(0,0,0,.35)"
        w="100%"
        h="60%"
      >

        {/* Περιεχόμενο κριτικής */}
        <Stack spacing={{ base: 3, md: 4 }} h="100%" overflow="hidden">
        <HStack spacing={3} align="flex-start">
          <Avatar size="sm" name={r.author_name} src={r.profile_photo_url || undefined} />
          <Stack spacing={1} minW={0} flex="1">
            {/* Γραμμή 1: Όνομα + Χρόνος */}
            <HStack spacing={2} alignItems="baseline">
              <Text
                fontWeight="bold"
                noOfLines={1} // Περιορισμός σε 1 γραμμή
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                overflow="hidden"
              >
                {r.author_name}
              </Text>
              <Text
                ml="auto"
                fontSize="sm"
                opacity={0.8}
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
              >
                {r.relative_time_description || formatDateFromUnix(r.time)}
              </Text>
            </HStack>

            {/* Γραμμή 2: Αστέρια + badge */}
            <HStack spacing={1}>
              {Array.from({ length: 5 }).map((_, i) => (
                <StarIcon
                  key={i}
                  boxSize={3.5}
                  color={i < Math.round(r.rating) ? "yellow.400" : "gray.400"}
                />
              ))}
              <Badge ml={2} variant="subtle" colorScheme="yellow">
                {Number(r.rating || 0).toFixed(1)}
              </Badge>
            </HStack>
          </Stack>
        </HStack>


          <Heading
            size={{ base: "sm", md: "sm" }}
            noOfLines={1} // Περιορισμός σε 1 γραμμή
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            overflow="hidden"
          >
            {t("reviews_heading")}
          </Heading>

          <Text
            fontSize={{ base: "md", md: "lg" }}
            noOfLines={3} // Περιορισμός σε 3 γραμμές
            textOverflow="ellipsis"
            overflow="hidden"
          >
            {r.text}
          </Text>
        </Stack>
      </Box>

      {/* Dots */}
      {len > 1 && (
        <HStack spacing={2} justify="center" mt={3}>
          {reviewsToShow.map((_, i) => (
            <Box
              key={i}
              w={i === index ? 8 : 2}
              h="2"
              borderRadius="full"
              bg={i === index ? "blackAlpha.900" : "blackAlpha.500"}
              transition="all .2s ease"
              cursor="pointer"
              onClick={() => goTo(i)}
            />
          ))}
        </HStack>
      )}

      {/* Google Reviews Button */}
      <Box mt={8} textAlign="center">
        <Box
          as="a"
          href="https://share.google/LcyvyyV6zcWuD3LUD"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Box
            as="button"
            display="inline-flex"
            alignItems="center"
            gap={2}
            px={6}
            py={2}
            borderRadius="md"
            bg="black"
            color="white"
            fontWeight="bold"
            fontSize={{ base: "md", md: "lg" }}
            boxShadow="md"
            _hover={{
              bg: "blackAlpha.800",
              transform: "translateX(6px)",
              transition: "all 0.2s",
            }}
            transition="all 0.2s"
            width="auto"
          >
            {t("reviews_google_button")}
            <ChevronRightIcon boxSize={5} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
